export const supportedLocales = ["en", "vi", "es", "pt", "fr", "de"] as const;
export type Locale = (typeof supportedLocales)[number];

const en = {
  privateAi: "Private AI on your device",
  workspace: "Workspace",
  history: "History",
  noHistory: "Your private history will appear here.",
  inputLabel: "Working material",
  inputHint: "Paste text or add a local file. Nothing is uploaded.",
  run: "Run privately",
  clear: "Clear",
  export: "Export",
  import: "Import",
  deleteAll: "Delete all",
  privacyTitle: "Your content stays here",
  privacyBody: "Prompts and results are processed and stored on this device.",
  model: "Runtime",
  checking: "Checking device…",
  webgpuStrong: "WebGPU · 1.7B",
  webgpuMobile: "WebGPU · 0.6B",
  wasm: "WASM fallback",
  offline: "Runtime bridge is not available yet.",
  guestQuota: "3 private runs per day",
  upgrade: "Unlock all 50 tools",
  sensitive: "Human review is required. This tool must not make consequential decisions.",
  result: "Result",
  locale: "Language",
  theme: "Theme",
  auto: "Auto",
} as const;

export type TranslationKey = keyof typeof en;
type Dictionary = Record<TranslationKey, string>;

const dictionaries: Record<Locale, Dictionary> = {
  en,
  vi: {
    privateAi: "AI riêng tư trên thiết bị",
    workspace: "Không gian làm việc",
    history: "Lịch sử",
    noHistory: "Lịch sử riêng tư sẽ xuất hiện tại đây.",
    inputLabel: "Nội dung làm việc",
    inputHint: "Dán văn bản hoặc thêm tệp local. Không có gì được tải lên.",
    run: "Chạy riêng tư",
    clear: "Xóa nội dung",
    export: "Xuất",
    import: "Nhập",
    deleteAll: "Xóa tất cả",
    privacyTitle: "Nội dung chỉ ở trên thiết bị",
    privacyBody: "Prompt và kết quả được xử lý và lưu trên thiết bị này.",
    model: "Bộ máy",
    checking: "Đang kiểm tra thiết bị…",
    webgpuStrong: "WebGPU · 1.7B",
    webgpuMobile: "WebGPU · 0.6B",
    wasm: "Dự phòng WASM",
    offline: "Cầu nối model chưa sẵn sàng.",
    guestQuota: "3 lượt riêng tư mỗi ngày",
    upgrade: "Mở khóa toàn bộ 50 công cụ",
    sensitive: "Bắt buộc con người xem xét. Công cụ không được đưa ra quyết định hệ trọng.",
    result: "Kết quả",
    locale: "Ngôn ngữ",
    theme: "Giao diện",
    auto: "Tự động",
  },
  es: {
    privateAi: "IA privada en tu dispositivo", workspace: "Espacio de trabajo", history: "Historial",
    noHistory: "Tu historial privado aparecerá aquí.", inputLabel: "Material de trabajo",
    inputHint: "Pega texto o añade un archivo local. Nada se sube.", run: "Ejecutar en privado",
    clear: "Limpiar", export: "Exportar", import: "Importar", deleteAll: "Borrar todo",
    privacyTitle: "Tu contenido se queda aquí", privacyBody: "Las entradas y resultados se procesan y guardan en este dispositivo.",
    model: "Motor", checking: "Comprobando dispositivo…", webgpuStrong: "WebGPU · 1.7B",
    webgpuMobile: "WebGPU · 0.6B", wasm: "Alternativa WASM", offline: "El motor aún no está disponible.",
    guestQuota: "3 usos privados al día", upgrade: "Desbloquea las 50 herramientas",
    sensitive: "Se requiere revisión humana. Esta herramienta no debe tomar decisiones importantes.",
    result: "Resultado", locale: "Idioma", theme: "Tema", auto: "Automático",
  },
  pt: {
    privateAi: "IA privada no seu dispositivo", workspace: "Espaço de trabalho", history: "Histórico",
    noHistory: "Seu histórico privado aparecerá aqui.", inputLabel: "Material de trabalho",
    inputHint: "Cole texto ou adicione um arquivo local. Nada é enviado.", run: "Executar em privado",
    clear: "Limpar", export: "Exportar", import: "Importar", deleteAll: "Excluir tudo",
    privacyTitle: "Seu conteúdo fica aqui", privacyBody: "Entradas e resultados são processados e salvos neste dispositivo.",
    model: "Motor", checking: "Verificando dispositivo…", webgpuStrong: "WebGPU · 1.7B",
    webgpuMobile: "WebGPU · 0.6B", wasm: "Alternativa WASM", offline: "O motor ainda não está disponível.",
    guestQuota: "3 usos privados por dia", upgrade: "Desbloqueie as 50 ferramentas",
    sensitive: "Revisão humana obrigatória. Esta ferramenta não deve tomar decisões importantes.",
    result: "Resultado", locale: "Idioma", theme: "Tema", auto: "Automático",
  },
  fr: {
    privateAi: "IA privée sur votre appareil", workspace: "Espace de travail", history: "Historique",
    noHistory: "Votre historique privé apparaîtra ici.", inputLabel: "Contenu de travail",
    inputHint: "Collez du texte ou ajoutez un fichier local. Rien n'est envoyé.", run: "Exécuter en privé",
    clear: "Effacer", export: "Exporter", import: "Importer", deleteAll: "Tout supprimer",
    privacyTitle: "Votre contenu reste ici", privacyBody: "Les entrées et résultats sont traités et stockés sur cet appareil.",
    model: "Moteur", checking: "Vérification de l’appareil…", webgpuStrong: "WebGPU · 1.7B",
    webgpuMobile: "WebGPU · 0.6B", wasm: "Repli WASM", offline: "Le moteur n’est pas encore disponible.",
    guestQuota: "3 usages privés par jour", upgrade: "Débloquer les 50 outils",
    sensitive: "Une validation humaine est requise. Cet outil ne doit pas prendre de décision importante.",
    result: "Résultat", locale: "Langue", theme: "Thème", auto: "Auto",
  },
  de: {
    privateAi: "Private KI auf deinem Gerät", workspace: "Arbeitsbereich", history: "Verlauf",
    noHistory: "Dein privater Verlauf erscheint hier.", inputLabel: "Arbeitsmaterial",
    inputHint: "Text einfügen oder lokale Datei hinzufügen. Nichts wird hochgeladen.", run: "Privat ausführen",
    clear: "Leeren", export: "Exportieren", import: "Importieren", deleteAll: "Alles löschen",
    privacyTitle: "Deine Inhalte bleiben hier", privacyBody: "Eingaben und Ergebnisse werden auf diesem Gerät verarbeitet und gespeichert.",
    model: "Laufzeit", checking: "Gerät wird geprüft…", webgpuStrong: "WebGPU · 1.7B",
    webgpuMobile: "WebGPU · 0.6B", wasm: "WASM-Fallback", offline: "Die Laufzeit ist noch nicht verfügbar.",
    guestQuota: "3 private Läufe pro Tag", upgrade: "Alle 50 Werkzeuge freischalten",
    sensitive: "Menschliche Prüfung ist erforderlich. Dieses Werkzeug darf keine wichtigen Entscheidungen treffen.",
    result: "Ergebnis", locale: "Sprache", theme: "Design", auto: "Automatisch",
  },
};

export const localeNames: Record<Locale, string> = {
  en: "English", vi: "Tiếng Việt", es: "Español", pt: "Português", fr: "Français", de: "Deutsch",
};

export function resolveLocale(value?: string | null): Locale {
  const normalized = value?.toLowerCase().split("-")[0] as Locale | undefined;
  return normalized && supportedLocales.includes(normalized) ? normalized : "en";
}

export function createI18n(initial?: string | null) {
  let locale = resolveLocale(initial ?? localStorage.getItem("tikvn.locale") ?? navigator.language);
  return {
    get locale() { return locale; },
    setLocale(next: Locale) { locale = next; localStorage.setItem("tikvn.locale", next); document.documentElement.lang = next; },
    t(key: TranslationKey) { return dictionaries[locale][key] ?? en[key]; },
  };
}
